import "./style.scss";
import React, { useState, useRef, useEffect, useImperativeHandle, useMemo } from "react";
import Input from "../../elements/input";
import Dropdown from "../../elements/dropdown";
import { BodyText, PrimaryLink } from "../../elements";
import { S_LANG, webStorage } from "../../utils/webStorage";
import { isBizNull } from "../../utils";
import { useSelector } from "react-redux";
import { selectUserState, staffInfoSelector } from "../../selectors/application";
import { pdpPersonalInfoSelector } from "../../selectors/pdp";
import { getCountryOptions } from "../../services/siteService";
import { dictTypes } from "../../utils/lynkco-constants";
import InputPhoneNumber from "../../elements/InputPhoneNumber";
import Heading from "../../elements/heading";
import getSiteInfo from "../../utils/getSiteInfo";
import useChangePhoneNumberModal from "../../hooks/useChangePhoneNumberModal";
import ResetPassword from "../../components/ResetPassword";
import { isArray } from "lodash";
import { useDictionaryOptions } from "../../hooks/useDictionaryOptions";
import SelectVerificationMethodModal from "../../react-components/select-verification-method-modal";
import "./style.scss";

const PdpPersonalInformation = React.forwardRef((props, ref) => {
  /*Redux start*/
  const user = useSelector(selectUserState);
  const staffInfo = useSelector(staffInfoSelector);
  const personalInfo = useSelector(pdpPersonalInfoSelector);
  /*Redux end*/
  const { fields, userIdentity, isDisabled, checkUpdateRequired, selectVerificationMethodModal, mfaModal } = props;

  const {
    firstNameLabel,
    firstNameMissingMessage,
    lastNameLabel,
    lastNameMissingMessage,
    socialSecurityNumberLabel,
    socialSecurityNumberMissingMessage,
    socialSecurityNumberWarningMessage,
    changePhoneNumberModal,
    birthdayInfoMessage,
    birthdayLabel,
    birthdayMissingMessage,
    birthdayUnder18Message,
    birthdayWarningMessage,
    cityLabel,
    cityMissingMessage,
    countryLabel,
    countryMissingMessage,
    genderLabel,
    genderMissingMessage,
    idLabel,
    idMissingMessage,
    idWarningMessage,
    idTypeLabel,
    idTypes,
    idTypeMissingMessage,
    identificationHeading,
    identificationHeadingType,
    personalNumberLabel,
    personalNumberInfoMessage,
    personalNumberMissingMessage,
    personalNumberWarningMessage,
    placeOfBirthHeading,
    placeOfBirthHeadingType,
    postalCodeLabel,
    postalCodeMissingMessage,
    postalCodeWarningMessage
  } = fields;
  const lang = webStorage.get(S_LANG);
  const [siteInfo, setSiteInfo] = useState({});
  /*Name Declarations*/
  const [firstName, setFirstName] = useState(null);
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);
  /*Gender Declarations*/
  const [gender, setGender] = useState();
  const [genderHasError, setGenderHasError] = useState(null);
  /*SSN Declarations*/
  const [socialSecurityNumberIsValid, setSocialSecurityNumberIsValid] = useState(true);
  const [socialSecurityNumberError, setSocialSecurityNumberError] = useState(null);
  /*Personal number Declarations*/
  const [personalNumberIsValid, setPersonalNumberIsValid] = useState(true);
  const [personalNumberError, setPersonalNumberError] = useState(null);
  /*Date of Birth Declarations*/
  const [dateOfBirth, setDateOfBirth] = useState();
  const [dateOfBirthIsValid, setDateOfBirthIsValid] = useState(true);
  const [dateOfBirthError, setDateOfBirthError] = useState(null);
  const [dateOfBirthUnder18, setDateOfBirthUnder18] = useState(null);
  /*Gender Declarations*/
  const [selectedIdType, setSelectedIdType] = useState();
  const [idTypeError, setIdTypeError] = useState(null);
  /*Id number Declarations*/
  const [ssn, setSSN] = useState();
  const [idNumber, setIdNumber] = useState();
  const [idNumberIsValid, setIdNumberIsValid] = useState(true);
  const [idNumberError, setIdNumberError] = useState(null);
  /*City Declarations*/
  const [city, setCity] = useState();
  const [cityError, setCityError] = useState(null);
  /*Postal code Declarations*/
  const [postalCode, setPostalCode] = useState();
  const [postalCodeError, setPostalCodeError] = useState(null);
  const [postalCodeIsValid, setPostalCodeIsValid] = useState(true);
  /*Country Declarations*/
  const [country, setCountry] = useState();
  const [countryList, setCountryList] = useState([]);
  const [countryError, setCountryError] = useState(null);
  const [genderList = [], idTypeList = []] = useDictionaryOptions([dictTypes.gender, dictTypes.idType]);
  const idTypeOptions = useMemo(() => {
    if (idTypes.length === 0 || !idTypeList || idTypeList.length === 0) {
      return [];
    } else {
      return idTypeList.filter(x => idTypes.some(y => y.fields.key.value === x.code));
    }
  }, [idTypes, idTypeList]);
  /*Phone Declarations*/
  const { phoneProps, needUpdate, validatePhoneFields, verificationModalProps, ctaChangePhoneNumber, changePhoneNumberModalProps, resetPasswordProps, updatedMessage, disclamerChangePhone } = useChangePhoneNumberModal({
    fields: changePhoneNumberModal?.fields,
    verifyEmailForm: mfaModal?.fields?.verifyEmailForm,
    selectVerificationMethodModal,
    checkUpdateRequired
  });

  const [formError, setFormError] = useState(null);

  const isInStore = useMemo(() => !!staffInfo, [staffInfo]);

  const rootEl = useRef();
  const getIdentityNumber = typeCode => {
    const ide = userIdentity?.countryIdentityInfo && isArray(userIdentity?.countryIdentityInfo) ? userIdentity?.countryIdentityInfo.find(x => x.idType === typeCode) : null;
    return ide?.idNumber;
  };
  useEffect(() => {
    setFirstName(personalInfo?.firstname || userIdentity?.firstName);
    setLastName(personalInfo?.lastName || userIdentity?.lastName);
    setPostalCode(personalInfo?.postalCode || userIdentity?.postalCode);
    setDateOfBirth(personalInfo?.dateOfBirth || userIdentity?.birthday);
    setCity(personalInfo?.city || userIdentity?.cityOfBirth);
  }, [userIdentity, personalInfo]);
  useEffect(() => {
    const _gender = genderList.find(x => x.code === (personalInfo?.gender || userIdentity?.gender));
    setGender(_gender);
  }, [genderList, personalInfo, userIdentity]);
  useEffect(() => {
    if (userIdentity) {
      let _selectedIdType;
      if (identificationHeading.value) {
        if (idTypeOptions) {
          _selectedIdType = idTypeOptions.find(x => x.code === personalInfo?.idType);
          const idn = getIdentityNumber(personalInfo?.idType);
          setIdNumber(idn);
        }
      }
      const _ssn = getIdentityNumber("IDTYPE03");
      setSSN(_ssn);
      setSelectedIdType(_selectedIdType);
    }
  }, [personalInfo, idTypeOptions, userIdentity]);
  useEffect(() => {
    if (placeOfBirthHeading.value && countryList.length > 0) {
      const country = countryList.find(x => x.code === personalInfo?.country || x.code === userIdentity?.countryOfBirth);
      setCountry(country);
    }
  }, [countryList, personalInfo, userIdentity]);

  const init = async () => {
    const [_siteInfo, countries = []] = await Promise.all([getSiteInfo(), getCountryOptions()]);
    setSiteInfo(_siteInfo);
    setCountryList(countries);
  };

  useEffect(() => {
    init();
  }, []);

  useImperativeHandle(ref, () => ({
    setError(error) {
      setFormError(error);
    },
    validateForm() {
      return {
        isValid: validateFields(),
        formData: {
          firstName,
          lastName,
          gender: gender?.code,
          birthday: dateOfBirth || "",
          ssn,
          idType: selectedIdType?.code || "IDTYPE03",
          idNumber: idNumber || "",
          cityOfBirth: city || "",
          postalCodeOfBirth: postalCode || "",
          countryOfBirth: country?.code || "",
          phoneArea: user?.mobileNoCountryCode,
          phone: user?.mobileNo,
          unSave: true
        }
      };
    },
    getEl() {
      return rootEl.current;
    }
  }));
  const validateFields = () => {
    let valid = true;
    if (isBizNull(firstName)) {
      setFirstNameError(firstNameMissingMessage.value);
      valid = false;
    }
    if (isBizNull(lastName)) {
      setLastNameError(lastNameMissingMessage.value);
      valid = false;
    }
    if (socialSecurityNumberLabel.value) {
      if (isBizNull(idNumber)) {
        setSocialSecurityNumberError(socialSecurityNumberMissingMessage.value);
        valid = false;
      } else if (!socialSecurityNumberIsValid) {
        setSocialSecurityNumberError(socialSecurityNumberWarningMessage.value);
        valid = false;
      }
    }
    if (personalNumberLabel.value) {
      if (isBizNull(idNumber)) {
        setPersonalNumberError(personalNumberMissingMessage.value);
        valid = false;
      } else if (!personalNumberIsValid) {
        setPersonalNumberError(personalNumberWarningMessage.value);
        valid = false;
      }
    }
    if (isBizNull(gender)) {
      setGenderHasError(genderMissingMessage.value);
      valid = false;
    }
    if (birthdayLabel.value) {
      if (isBizNull(dateOfBirth)) {
        setDateOfBirthError(birthdayMissingMessage.value);
        valid = false;
      } else if (!dateOfBirthIsValid) {
        setDateOfBirthError(birthdayWarningMessage.value);
        valid = false;
      } else if (dateOfBirthUnder18) {
        setDateOfBirthError(birthdayUnder18Message.value);
        valid = false;
      }
    }
    if (identificationHeading.value) {
      if (isBizNull(selectedIdType)) {
        setIdTypeError(idTypeMissingMessage.value);
        valid = false;
      }
      if (isBizNull(idNumber)) {
        setIdNumberError(idMissingMessage.value);
        valid = false;
      } else if (!idNumberIsValid) {
        setIdNumberError(idWarningMessage.value);
        valid = false;
      }
    }
    if (placeOfBirthHeading.value && isBizNull(city)) {
      setCityError(cityMissingMessage.value);
      valid = false;
    }
    if (placeOfBirthHeading.value && isBizNull(postalCode)) {
      setPostalCodeError(postalCodeMissingMessage.value);
      valid = false;
    }
    if (placeOfBirthHeading.value && isBizNull(country)) {
      setCountryError(countryMissingMessage.value);
      valid = false;
    }

    valid = valid && validatePhoneFields();
    return valid;
  };

  const onAutocompleteLocation = address => {
    if (address.city) setCity(address.city);
    setPostalCode(address.postalCode ? address.postalCode : "");
    if (address.country && countryList) {
      const filteredCountry = countryList.filter(c => c.data.regionAbbr === address.country);

      if (filteredCountry && filteredCountry.length) {
        setCountry(filteredCountry[0]);
      }
    }
  };

  const firstNameProps = {
    label: firstNameLabel?.value,
    value: firstName,
    maxLength: 25,
    showError: firstNameError !== null,
    warningMessage: firstNameError,
    disabled: isDisabled,
    onFocus: evt => {
      setFirstNameError(null);
    },
    onBlur: evt => {
      setFirstName(evt.target.value);
    }
  };
  const lastNameProps = {
    label: lastNameLabel?.value,
    value: lastName,
    maxLength: 25,
    showError: lastNameError !== null,
    warningMessage: lastNameError,
    disabled: isDisabled,
    onFocus: evt => {
      setLastNameError(null);
    },
    onBlur: evt => {
      setLastName(evt.target.value);
    }
  };

  const socialSecurityNumberProps = {
    label: socialSecurityNumberLabel?.value,
    value: ssn,
    showError: socialSecurityNumberError !== null,
    warningMessage: socialSecurityNumberError,
    disabled: isDisabled,
    onChange(evt, isValid, isOver18, birthday) {
      setSocialSecurityNumberIsValid(isValid);
      if (!!birthday) {
        setDateOfBirth(birthday);
        setDateOfBirthUnder18(!isOver18);
        setSocialSecurityNumberIsValid(birthday !== dateOfBirth);
      }
    },
    onFocus: evt => {
      setSocialSecurityNumberError(null);
    },
    onBlur: evt => {
      setSSN(evt.target.value);
    }
  };

  const personalNumberProps = {
    label: personalNumberLabel?.value,
    value: ssn,
    showError: personalNumberError !== null,
    warningMessage: personalNumberError,
    infoMessage: personalNumberInfoMessage?.value,
    onChange(evt, isValid, isOver18, birthday) {
      setPersonalNumberIsValid(isValid);
      if (!!birthday) {
        setDateOfBirth(birthday);
        setDateOfBirthUnder18(!isOver18);
        setSocialSecurityNumberIsValid(birthday !== dateOfBirth);
      }
    },
    onFocus: evt => {
      setPersonalNumberError(null);
    },
    onBlur: evt => {
      setSSN(evt.target.value);
    }
  };

  const dateOfBirthProps = {
    label: birthdayLabel?.value,
    value: dateOfBirth,
    infoMessage: birthdayInfoMessage?.value,
    showError: !!dateOfBirthError,
    warningMessage: dateOfBirthError,
    type: "dateOfBirth",
    onChange(evt, isValid, isOver18) {
      setDateOfBirthIsValid(isValid);
      setDateOfBirthUnder18(!isOver18);
      if (!isValid) {
        setDateOfBirthError(birthdayWarningMessage.value);
      }
    },
    onBlur: evt => {
      setDateOfBirth(evt.target.value);
    }
  };

  const idNumberProps = {
    label: idLabel?.value,
    value: idNumber,
    showError: idNumberError !== null,
    warningMessage: idNumberError,
    onFocus: evt => {
      setIdNumberError(null);
    },
    onChange(evt, isValid, isOver18, birthday) {
      // setIdNumberIsValid(isValid);
      // if (!!birthday && !!birthdayLabel.value) {
      //   setDateOfBirth(birthday);
      //   setDateOfBirthUnder18(!isOver18);
      // }
    },
    onBlur: evt => {
      setIdNumber(evt.target.value);
    }
  };

  const cityProps = {
    label: cityLabel?.value,
    value: city,
    showError: cityError !== null,
    warningMessage: cityError,
    location: { onAutocomplete: onAutocompleteLocation, types: ["(cities)"] },
    onFocus: evt => {
      setCityError(null);
    },
    onBlur: evt => {
      setCity(evt.target.value);
    }
  };

  const postalCodeProps = {
    label: postalCodeLabel?.value,
    value: postalCode,
    showError: postalCodeError !== null,
    warningMessage: postalCodeError,
    location: { onAutocomplete: onAutocompleteLocation, types: ["(regions)"] },
    onFocus: evt => {
      setPostalCodeError(null);
    },
    onBlur(evt, isValid) {
      setPostalCode(evt.target.value);
      setPostalCodeIsValid(isValid);
      if (!isValid) {
        setPostalCodeError(postalCodeWarningMessage.value);
      }
    },
    onChange(evt, isValid) {
      setPostalCodeIsValid(isValid);
      if (!isValid) {
        setPostalCodeError(postalCodeWarningMessage.value);
      }
    }
  };

  const onGenderChange = option => {
    setGender(option);
  };

  const onGenderHasFocus = () => {
    setGenderHasError(false);
  };

  const onIdTypeChange = option => {
    setSelectedIdType(option);
    const idn = getIdentityNumber(option.code);
    setIdNumber(idn);
  };

  const onIdTypeHasFocus = () => {
    setIdTypeError(null);
  };

  const onCountryChange = option => {
    setCountry(option);
  };

  const onCountryHasFocus = () => {
    setCountryError(false);
  };
  const genderProps = {
    placeholder: genderLabel.value,
    warningMessage: genderMissingMessage.value,
    enableFilter: false,
    options: genderList,
    selectedOption: gender,
    showError: genderHasError,
    onFocus: onGenderHasFocus,
    onChange: option => onGenderChange(option)
  };
  return (
    <div className="pdp-personal-information">
      <form className="shipping-info-form" ref={rootEl}>
        <Input name="first-name" autocomplete="first-name" {...firstNameProps} />
        <Input name="family-name" autocomplete="family-name" {...lastNameProps} />
        <Dropdown {...genderProps} />
        <InputPhoneNumber {...phoneProps} />
        {ctaChangePhoneNumber && needUpdate && <BodyText html={disclamerChangePhone.value} />}
        {updatedMessage && <BodyText html={updatedMessage} />}
        {ctaChangePhoneNumber && needUpdate && (
          <div className="change-phone-number-button">
            <PrimaryLink {...ctaChangePhoneNumber} />
          </div>
        )}
        {personalNumberLabel && personalNumberLabel.value && <Input name="personal-number" type="ssn" {...personalNumberProps} />}
        {birthdayLabel && birthdayLabel.value && <Input name="date-of-birth" {...dateOfBirthProps} />}
        {socialSecurityNumberLabel && socialSecurityNumberLabel.value && <Input name="social-security-number" type="ssn" {...socialSecurityNumberProps} />}
        {identificationHeading && identificationHeading.value && (
          <div className="identification">
            <div className="identification-heading">
              <Heading type={identificationHeadingType} html={identificationHeading} typographyClass="type-h3" />
            </div>
            <Dropdown
              {...{
                placeholder: idTypeLabel.value,
                warningMessage: idTypeError,
                options: idTypeOptions,
                enableFilter: false,
                selectedOption: selectedIdType,
                showError: !!idTypeError,
                onFocus: onIdTypeHasFocus,
                onChange: option => onIdTypeChange(option)
              }}
            />
            <Input name="id-number" {...idNumberProps} />
          </div>
        )}
        {placeOfBirthHeading && placeOfBirthHeading.value && (
          <div className="place-of-birth">
            <div className="place-of-birth-heading">
              <Heading type={placeOfBirthHeadingType} html={placeOfBirthHeading} typographyClass="type-h3" />
            </div>
            <Input name="city" {...cityProps} />
            <Input type="postalCode" {...postalCodeProps} />
            <Dropdown
              {...{
                placeholder: countryLabel.value,
                warningMessage: countryMissingMessage.value,
                enableFilter: true,
                options: countryList,
                selectedOption: country,
                showError: countryError,
                onFocus: onCountryHasFocus,
                onChange: option => onCountryChange(option)
              }}
            />
          </div>
        )}
        {formError && <div className="failed-message">{formError}</div>}
      </form>
      {verificationModalProps && verificationModalProps.verifyEmailForm && <SelectVerificationMethodModal type="phoneChange" {...verificationModalProps} />}
      <ResetPassword {...resetPasswordProps} />
    </div>
  );
});
PdpPersonalInformation.displayName = "PdpPersonalInformation";
export default PdpPersonalInformation;
